import KanbanAPI from '../../../api/kanban';
import { types } from './mutations';

// troca rápida de funil/filtro dispara fetches concorrentes; só a resposta da
// requisição mais recente pode ser aplicada, senão o board mostra cards do
// funil anterior quando a resposta antiga chega por último
let fetchCardsRequestId = 0;

export default {
  async fetchPipelines({ commit, state }) {
    commit(types.SET_UI_FLAG, { isLoadingPipelines: true, loadError: null });
    try {
      const { data } = await KanbanAPI.getPipelines();
      commit(types.SET_PIPELINES, data.data);
      if (data.data.length > 0) {
        // recarregar não troca o funil que o usuário está vendo
        const current = data.data.find(p => p.id === state.activePipelineId);
        commit(types.SET_ACTIVE_PIPELINE, current?.id ?? data.data[0].id);
      }
    } catch (error) {
      commit(types.SET_UI_FLAG, {
        loadError:
          error.response?.data?.errors?.[0] ?? 'Erro ao carregar pipelines',
      });
    } finally {
      commit(types.SET_UI_FLAG, { isLoadingPipelines: false });
    }
  },

  async fetchCards({ commit, state }, filters) {
    if (!state.activePipelineId) return;
    const resolvedFilters =
      filters !== undefined ? filters : state.activeFilters;
    if (filters !== undefined) {
      commit(types.SET_ACTIVE_FILTERS, resolvedFilters);
    }
    fetchCardsRequestId += 1;
    const requestId = fetchCardsRequestId;
    commit(types.SET_UI_FLAG, { isLoadingCards: true });
    try {
      const { data } = await KanbanAPI.getCards(
        state.activePipelineId,
        resolvedFilters
      );
      if (requestId !== fetchCardsRequestId) return;
      const merged = data.data.map(incoming => {
        const existing = state.cards.find(c => c.id === incoming.id);
        if (!existing?.contact) return incoming;
        return {
          ...incoming,
          contact: {
            ...incoming.contact,
            additional_attributes:
              existing.contact.additional_attributes ??
              incoming.contact?.additional_attributes,
          },
        };
      });
      commit(types.SET_CARDS, merged);
    } finally {
      if (requestId === fetchCardsRequestId) {
        commit(types.SET_UI_FLAG, { isLoadingCards: false });
      }
    }
  },

  async moveCard(
    { commit },
    {
      cardId,
      columnId,
      position,
      afterCardId,
      lossReason,
      cancelReason,
      waitingReason,
    }
  ) {
    try {
      const { data } = await KanbanAPI.moveCard(cardId, columnId, position, {
        lossReason,
        cancelReason,
        waitingReason,
        afterCardId,
      });
      commit(types.UPDATE_CARD, data.data);
      return { success: true, data: data.data };
    } catch (error) {
      const errors = error.response?.data?.errors ?? ['Erro ao mover card'];
      return { success: false, errors };
    }
  },

  async createPipeline({ commit, state }, pipelineData) {
    try {
      const { data } = await KanbanAPI.createPipeline(pipelineData);
      commit(types.SET_PIPELINES, [...state.pipelines, data.data]);
      return data.data;
    } catch (error) {
      throw error.response?.data?.errors ?? ['Erro ao criar pipeline'];
    }
  },

  async updatePipeline({ commit, state }, { id, ...pipelineData }) {
    try {
      const { data } = await KanbanAPI.updatePipeline(id, pipelineData);
      const updated = state.pipelines.map(p => (p.id === id ? data.data : p));
      commit(types.SET_PIPELINES, updated);
      return data.data;
    } catch (error) {
      throw error.response?.data?.errors ?? ['Erro ao atualizar pipeline'];
    }
  },

  async deletePipeline({ commit, state }, id) {
    try {
      await KanbanAPI.deletePipeline(id);
      commit(types.REMOVE_PIPELINE, id);
      if (state.activePipelineId === id) {
        const next = state.pipelines.find(p => p.id !== id);
        commit(types.SET_ACTIVE_PIPELINE, next?.id ?? null);
      }
    } catch (error) {
      throw error.response?.data?.errors ?? ['Erro ao excluir pipeline'];
    }
  },

  async createCard({ commit }, cardData) {
    try {
      const { data } = await KanbanAPI.createCard(cardData);
      commit(types.ADD_CARD, data.data);
      return { success: true, data: data.data };
    } catch (error) {
      const errors = error.response?.data?.errors ?? ['Erro ao criar card'];
      return { success: false, errors };
    }
  },

  async updateCard({ commit, state }, { cardId, data }) {
    try {
      const { data: response } = await KanbanAPI.updateCard(cardId, data);
      const existing = state.cards.find(c => c.id === cardId);
      const merged = existing
        ? {
            ...response.data,
            contact: existing.contact
              ? {
                  ...response.data.contact,
                  additional_attributes:
                    existing.contact.additional_attributes ??
                    response.data.contact?.additional_attributes,
                }
              : response.data.contact,
          }
        : response.data;
      commit(types.UPDATE_CARD, merged);
      return { success: true, data: merged };
    } catch (error) {
      const errors = error.response?.data?.errors ?? ['Erro ao atualizar card'];
      return { success: false, errors };
    }
  },

  async deleteCard({ commit }, cardId) {
    try {
      await KanbanAPI.deleteCard(cardId);
      commit(types.REMOVE_CARD, cardId);
      return { success: true };
    } catch (error) {
      const errors = error.response?.data?.errors ?? ['Erro ao deletar card'];
      return { success: false, errors };
    }
  },

  setActivePipeline({ commit }, pipelineId) {
    commit(types.SET_ACTIVE_PIPELINE, pipelineId);
  },

  handleCardMovedEvent(
    { commit, state },
    { card_id: cardId, column_id: columnId, position, status }
  ) {
    const card = state.cards.find(c => c.id === cardId);
    if (card) {
      const update = { ...card, column_id: columnId, position };
      if (status !== undefined) update.status = status;
      commit(types.UPDATE_CARD, update);
    }
  },

  async handleCardUpdatedEvent({ commit, state }, { card_id: cardId }) {
    try {
      const { data } = await KanbanAPI.getCard(cardId);
      const card = data.data;
      // Evento chega para toda a conta (todos os pipelines). Só aplica se o
      // card já pertence ao board carregado (update) ou é do pipeline ativo —
      // nunca insere card de outro pipeline/categoria no state. Se o card
      // estava carregado mas migrou para outro pipeline, remove do state em
      // vez de atualizar, para não deixar dado obsoleto (pipeline errado)
      // parado em state.cards.
      const alreadyTracked = state.cards.some(c => c.id === card.id);
      if (card.pipeline_id === state.activePipelineId) {
        commit(types.UPDATE_CARD, card);
      } else if (alreadyTracked) {
        commit(types.REMOVE_CARD, card.id);
      }
    } catch (error) {
      // card foi para a lixeira: sai do board aberto
      if (error.response?.status === 404) commit(types.REMOVE_CARD, cardId);
    }
  },

  async bulkUpdateCards({ commit }, { ids, data }) {
    const { data: res } = await KanbanAPI.bulkUpdate(ids, data);
    commit(types.BULK_UPDATE_CARDS, res.data);
  },

  async bulkLabelCards({ commit }, { ids, label, action }) {
    const { data: res } = await KanbanAPI.bulkLabel(ids, label, action);
    commit(types.BULK_UPDATE_CARDS, res.data);
  },

  async bulkDeleteCards({ commit }, ids) {
    await KanbanAPI.bulkDestroy(ids);
    commit(types.BULK_REMOVE_CARDS, ids);
  },

  async bulkCreateTask(_, { ids, task }) {
    await KanbanAPI.bulkCreateTask(ids, task);
  },

  async bulkCreateNote(_, { ids, content }) {
    await KanbanAPI.bulkCreateNote(ids, content);
  },
};
